import React, { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Icon from '../../../components/AppIcon';
import { IMAGE_VERSION } from '../../../version.js';
import { useTranslation } from 'react-i18next';

gsap.registerPlugin(ScrollTrigger);



const CompanyStory = () => {
  const { t } = useTranslation('about');
  const milestones = [
    {
      year: "1992",
      description: t('milestone.1992', "PC Centar je osnovan kao specijalizovana prodavnica računarske opreme u Beogradu."),
      hasRedBox: false,
      position: "below"
    },
    {
      year: "1997",
      description: t('milestone.1997', "Distribucioni ugovori sa proizvođačima i širenje maloprodajne mreže."),
      hasRedBox: false,
      position: "above"
    },
    {
      year: "2001",
      description: t('milestone.2001') || "Uvođenje novih brendova i proširenje asortimana proizvoda.",
      hasRedBox: true,
      position: "below"
    },
    {
      year: "2007",
      description: t('milestone.2007') || "Privatni investitori ulaze u kompaniju i započinje značajan rast poslovanja.",
      hasRedBox: false,
      position: "above"
    },
    {
      year: "2010",
      description: t('milestone.2010') || "Akvizicije i širenje distributivnih kanala na region Balkana.",
      hasRedBox: false,
      position: "below"
    },
    {
      year: "2013",
      description: t('milestone.2013') || "Uspostavljanje strateških partnerstava i proširenje ponude proizvoda i usluga.",
      hasRedBox: false,
      position: "above"
    },
    {
      year: "2015",
      description: t('milestone.2015') || "Početak saradnje sa proizvođačima mobilnih uređaja i dodataka.",
      hasRedBox: true,
      position: "below"
    },
    {
      year: "2016",
      description: t('milestone.2016') || "PC Centar postaje član ComputerLand grupe kompanija.",
      hasRedBox: false,
      position: "above"
    },
    {
      year: "2017",
      description: t('milestone.2017') || "Potpisan ugovor o ekskluzivnoj saradnji sa Xiaomi Communications.",
      hasRedBox: true,
      position: "below"
    },
    {
      year: "2018",
      description: t('milestone.2018') || "Otvaranje novih prodavnica i proširenje saradnje sa partnerima u regionu.",
      hasRedBox: false,
      position: "above"
    },
    {
      year: "2019",
      description: t('milestone.2019') || "Proširenje poslovanja na međunarodnom nivou i razvoj novih tržišta.",
      hasRedBox: true,
      position: "below"
    },
    {
      year: "2020",
      description: t('milestone.2020') || "Potpuna digitalna transformacija poslovanja i ulazak u e‑trgovinu.",
      hasRedBox: false,
      position: "above"
    },
    {
      year: "2021",
      description: t('milestone.2021') || "Otvaranje novih prodavnica i jačanje maloprodajnog lanca širom Srbije.",
      hasRedBox: true,
      position: "below"
    },
    {
      year: "2022",
      description: t('milestone.2022') || "Uspostavljanje liderske pozicije na tržištu i nastavak dinamičnog rasta.",
      hasRedBox: false,
      position: "above"
    }
  ];

  // Refs for scrolling
  const sectionRef = useRef(null);
  const timelineRef = useRef(null);
  const timelineTrackRef = useRef(null);
  


  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const track = timelineTrackRef.current;
      if (!track) return;

      const getScrollDistance = () => {
        return -(track.scrollWidth - window.innerWidth + 400);
      };

      gsap.to(track, {
        x: getScrollDistance,
        ease: "none",
        force3D: true,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: () => `+=${track.scrollWidth + 600}`,
          scrub: 0.5,
          pin: true,
          anticipatePin: 1,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-16 lg:py-24 relative overflow-hidden bg-white h-screen"
      style={{ contain: 'paint' }}
    >
      
        
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-secondary opacity-95 pointer-events-none"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 via-transparent to-accent/10 pointer-events-none"></div>
      
      {/* Dark theme overlay for perfect text contrast */}
      <div className="absolute inset-0 hero-dark-overlay opacity-70 [data-theme='dark']:opacity-100 pointer-events-none"></div>
      
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 opacity-10">
          <svg viewBox="0 0 48 48" className="w-full h-full">
            <polygon 
              points="24,2 38,12 38,28 24,38 10,28 10,12" 
              fill="var(--color-accent)"
            />
          </svg>
        </div>
        <div className="absolute bottom-20 right-10 w-24 h-24 opacity-10">
          <svg viewBox="0 0 48 48" className="w-full h-full">
            <polygon 
              points="24,2 38,12 38,28 24,38 10,28 10,12" 
              fill="var(--color-surface)"
            />
          </svg>
        </div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 opacity-5">
          <svg viewBox="0 0 48 48" className="w-full h-full">
            <polygon 
              points="24,2 38,12 38,28 24,38 10,28 10,12" 
              fill="var(--color-primary)"
            />
          </svg>
        </div>
      </div>
      
      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="font-heading font-bold text-4xl lg:text-5xl xl:text-5xl text-foreground leading-tight">
              {t('keyMilestones') || 'Ključne etape'}
              <span className="block text-accent mt-2">PC Centar</span>
            </h1>
          {/* Red square with diamond icon */}
          
          <div className="inline-flex items-center justify-center mb-6">
            <div className="relative">
              <img 
                src={`/assets/images/logo2.png?ver=${IMAGE_VERSION}`} 
                alt="PC Centar Logo" 
                width="128" 
                height="128" 
                className="object-contain rounded-xl shadow-lg border border-accent bg-white" 
                style={{ background: '#fff' }}
              />
            </div>
          </div>
        </div>

        {/* Timeline with navigation */}
        <div className="relative">
          {/* Scrollable timeline container */}
          <div 
            ref={timelineRef}
            className="overflow-hidden relative px-20"
            style={{ 
              height: '600px'
            }}
          >
            {/* Timeline track - this gets animated horizontally */}
            <div 
              ref={timelineTrackRef}
              className="relative will-change-transform"
              style={{ width: `${(milestones.length * 178) + 150}px`, height: '600px' }}
            >
              {/* Horizontal timeline line */}
              <div className="absolute h-1 bg-black transform -translate-y-1/2 z-0" 
                   style={{ 
                     top: '50%',
                     left: '-40px',
                     width: `${(milestones.length * 178) + 200}px`
                   }}></div>
              
              {/* Timeline items container */}
              <div className="flex justify-between items-start relative min-w-max" style={{ width: `${(milestones.length * 140) + 80}px`, height: '600px' }}>
              {milestones.map((milestone, idx) => {
                const isYearAbove = milestone.position === "above";
                const isHighlighted = [1, 3, 5, 7, 9, 11, 13].includes(idx);
                
                return (
                  <div 
                    key={idx} 
                    className="flex flex-col items-center relative w-40 mx-2" 
                    style={{ height: '600px' }}
                    // SEO: Označavamo svaki milestoun kao zaseban članak/događaj
                    role="article"
                    aria-label={`Događaj iz ${milestone.year} godine`}
                    itemScope
                    itemType="https://schema.org/Event"
                  >
                    
                    {/* LEVEL 1: Timeline dot on horizontal line */}
                    <div 
                      className="rounded-full bg-black z-10"
                      style={{ 
                        position: 'absolute', 
                        top: '50%', 
                        left: '50%', 
                        transform: 'translate(-50%, -50%)',
                        width: '16px',
                        height: '16px'
                      }}
                    ></div>

                    {/* LEVEL 2: Vertical line from horizontal line to YEAR */}
                    <div className="absolute w-px bg-black z-5" style={{ 
                      left: '50%',
                      transform: 'translateX(-50%)',
                      top: isYearAbove ? 'calc(50% + 3px)' : 'calc(47% - 3px)',
                      height: '25px'
                    }}></div>

                    {/* LEVEL 3: YEAR marker */}
                    <div className={`absolute z-20 left-1/2 transform -translate-x-1/2`}
                        style={{ 
                          top: isYearAbove ? 'calc(50% + 30px)' : 'calc(45% - 30px)',
                          transform: 'translateX(-50%)'
                        }}>
                      <div className="text-center">
                        {milestone.hasRedBox ? (
                          <div className="bg-red-500 text-white px-2 py-1">
                            <span className="font-bold text-lg" itemProp="startDate">{milestone.year}</span>
                          </div>
                        ) : (
                          <span className="text-2xl font-bold text-black" itemProp="startDate">
                            {milestone.year}
                          </span>
                        )}
                      </div>
                      
                      {/* Settings icon for specific years */}
                      {(idx === 3 || idx === 7) && (
                        <Icon name="Settings" size={16} className="text-red-500 mx-auto mt-1" />
                      )}
                    </div>

                    {/* LEVEL 4: Vertical line from YEAR to DESCRIPTION CARD */}
                    <div className="absolute w-px bg-black z-5" style={{ 
                      left: '50%',
                      transform: 'translateX(-50%)',
                      top: isYearAbove ? 'calc(51.5% + 55px)' : 'calc(33% - 55px)',
                      height: '90px'
                    }}></div>

                    {/* LEVEL 5: DESCRIPTION CARD */}
                    <div 
                      className={`absolute p-4 `}
                      style={{ 
                        top: isYearAbove ? 'calc(37% + 135px)' : 'calc(57% - 215px)',
                        left: '50%',
                        transform: 'translateX(5%)',
                        width: '240px',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        borderRadius: '0px',
                        backgroundColor: 'transparent',
                      }}
                    >
                      <p className="text-left leading-relaxed" itemProp="description">
                        {milestone.description.split(' ').map((word, wordIdx) => {
                          const redHighlightWords = ['PC Centar', 'ComputerLand', 'Xiaomi', 'Communications', 'stakeholder', 'acquired'];
                          const shouldBeRed = redHighlightWords.some(redWord => 
                            word.toLowerCase().includes(redWord.toLowerCase())
                          );
                          
                          return (
                            <span key={wordIdx} className={shouldBeRed ? 'font-bold text-red-500' : ''}>
                              {word}{wordIdx < milestone.description.split(' ').length - 1 ? ' ' : ''}
                            </span>
                          );
                        })}
                      </p>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          </div>
        </div>

        
      </div>
    </section>
  );
};

export default CompanyStory;